import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);

    // Encrypt Data
    const encryptData = (data) => {
        try {
            let jsonString = JSON.stringify(data);
            let encryptedString = btoa(
                jsonString
                    .split("")
                    .map(char => String.fromCharCode(char.charCodeAt(0) + 3))
                    .join("")
            );
            return encryptedString;
        } catch (error) {
            console.error("Encryption error:", error);
            return "";
        }
    };

    // Decrypt Data
    const decryptData = (encryptedData) => {
        try {
            let decodedString = atob(encryptedData);
            let decryptedString = decodedString
                .split("")
                .map(char => String.fromCharCode(char.charCodeAt(0) - 3))
                .join("");
            return JSON.parse(decryptedString);
        } catch (error) {
            console.error("Decryption error:", error);
            return [];
        }
    };

    // Load cart from local storage on mount
    useEffect(() => {
        const storedCart = localStorage.getItem("cart");
        if (storedCart) {
            setCart(decryptData(storedCart));
        }
    }, []);

    // Save cart to local storage (encrypted)
    const saveCart = (cartData) => {
        localStorage.setItem("cart", encryptData(cartData));
    };

    // Get price based on country & type
    const getPrice = (item, country, selectedDay) => {
        if (item?.discountAtOnce) {
            return country === "PK"
                ? (item.OriginalPricePak - (item.OriginalPricePak * item.discountAtOnce) / 100).toFixed(0)
                : (item.OriginalPriceUSA - (item.OriginalPriceUSA * item.discountAtOnce) / 100).toFixed(0);
        }
        return country === "PK"
            ? item.PriceAfterDiscountPak || (selectedDay === "day1Pak" ? item.endpriceforpak : item.priceforpak) || item.OriginalPricePak
            : item.PriceAfterDiscountUSA || (selectedDay === "day1USA" ? item.endpriceforus : item.priceforus) || item.OriginalPriceUSA;
    };

    // Add to Cart (Prevent Duplicates, Update Quantity Instead)
    const addToCart = (item, country, selectedDay, selectedTimeSlot) => {
        setCart((prevCart) => {
            const itemPrice = parseFloat(getPrice(item, country, selectedDay));
            const isQurbani = !item.categoryId;

            let updatedCart;

            if (isQurbani) {
                const uniqueId = `${item.id}_${Math.floor(Math.random() * 100000)}`;

                const newItem = {
                    id: uniqueId,
                    originalId: item.id,
                    type: "qurbani",
                    name: item.title,
                    price: itemPrice,
                    quantity: 1,
                    total: itemPrice,
                    image: item.QurbaniImages?.[0]?.imageUrl,
                    country,
                    selectedDay: selectedDay || "",
                    selectedHour: selectedTimeSlot || ""
                };

                updatedCart = [...prevCart, newItem];
            } else {
                const existingItem = prevCart.find(
                    (cartItem) => cartItem.id === item.id && cartItem.type === "product"
                );

                if (existingItem) {
                    updatedCart = prevCart.map((cartItem) =>
                        cartItem.id === item.id && cartItem.type === "product"
                            ? {
                                ...cartItem,
                                quantity: cartItem.quantity + 1,
                                total: (cartItem.quantity + 1) * cartItem.price
                            }
                            : cartItem
                    );
                } else {
                    const newItem = {
                        id: item.id,
                        type: "product",
                        name: item.productName,
                        price: itemPrice,
                        quantity: 1,
                        total: itemPrice,
                        image: item.ProductImages?.[0]?.imageUrl,
                        country,
                        selectedDay: selectedDay || "",
                        selectedHour: selectedTimeSlot || ""
                    };

                    updatedCart = [...prevCart, newItem];
                }
            }

            saveCart(updatedCart);
            toast.success("Product added to cart successfully!");
            return updatedCart;
        });
    };



    // Remove from Cart
    const removeFromCart = (productId) => {
        setCart((prevCart) => {
            const updatedCart = prevCart.filter(item => item.id !== productId);
            saveCart(updatedCart);
            return updatedCart;
        });
    };

    // Update Quantity
    const updateQuantity = (id, newQuantity) => {
        setCart((prevCart) => {
            const updatedCart = prevCart.map((item) =>
                item.id === id
                    ? { ...item, quantity: newQuantity, total: item.price * newQuantity }
                    : item
            );
            saveCart(updatedCart);
            return updatedCart;
        });
    };

    // Get Complete Total Price of the Cart
    const getCartTotal = () => {
        return cart.reduce((total, item) => total + item.total, 0).toFixed(0);
    };

    return (
        <CartContext.Provider value={{ cart, setCart, addToCart, removeFromCart, updateQuantity, getCartTotal }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
